import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  Search, MapPin, ShieldCheck, FileSignature, Wallet, Star, ChevronRight,
  UserRound, Wrench, Building2, ClipboardList, HardHat, Briefcase, Laptop,
  Truck, Home as HomeIcon, Package, MoreHorizontal, ArrowRight, X,
} from "lucide-react";
import { Tabs, Section } from "../components/ui/kit";
import { PersonCard, ServiceCard, CompanyCard, JobCard, ProjectCard } from "../components/cards";
import { categories, trust, stats } from "../data/mock";

const trustIcons: Record<string, any> = { ShieldCheck, FileSignature, Wallet, Star };
const catIcons: Record<string, any> = { HardHat, Briefcase, Laptop, Building2, Truck, Home: HomeIcon, Package, MoreHorizontal };

const actionCards = [
  { color: "bg-navy-50 text-navy", accent: "text-navy", icon: UserRound, title: "Find Work",
    desc: "Post your resume and discover job opportunities.", items: ["Full-time & Part-time", "Contractual Jobs", "Freelance & Projects"], cta: "Post Your Resume", btn: "btn-navy", href: "/post/resume" },
  { color: "bg-brand-green/10 text-brand-greendark", accent: "text-brand-greendark", icon: Wrench, title: "Offer Your Services",
    desc: "Advertise your skills and receive direct inquiries.", items: ["Skilled Workers", "Freelancers", "Licensed Professionals"], cta: "List a Service", btn: "btn-green", href: "/post/service" },
  { color: "bg-brand-gold/15 text-brand-gold", accent: "text-brand-gold", icon: Building2, title: "Promote Your Company",
    desc: "Create a company page and showcase your services.", items: ["Company Profile", "Service Listings", "Portfolio & Projects"], cta: "Create Company Profile", btn: "btn-gold", href: "/post/company" },
  { color: "bg-brand-red/10 text-brand-reddark", accent: "text-brand-reddark", icon: ClipboardList, title: "Post Jobs & Contracts",
    desc: "Hire employees, request quotes, or invite bids.", items: ["Job Openings", "Service Requests", "Projects & Bidding"], cta: "Post a Job or Project", btn: "btn-red", href: "/post/job" },
];

// Home search tab labels → Marketplace tab keys
const SEARCH_TAB_TO_MARKET: Record<string, string> = {
  People: "People", Services: "Services", Companies: "Companies", Jobs: "Jobs", "Projects & Bids": "Projects",
};

// Friendly category label → concise keyword the marketplace filters on
// (aligns with the marketplace's own category options where possible).
const CATEGORY_KEYWORD: Record<string, string> = {
  "Construction & Trade": "Construction",
  "Professional Services": "Professional",
  "IT & Digital Services": "IT",
  "Business Services": "Business",
  "Logistics & Transport": "Logistics",
  "Home & Personal": "Home",
  "Supplies & Equipment": "Supplies",
  "Others": "",
};

export default function Home() {
  const { user } = useAuth();
  const [searchTab, setSearchTab] = useState("People");
  const [featTab, setFeatTab] = useState("Recommended");
  // Featured section pulls the SAME gated live data the Marketplace uses, so the
  // landing page and Marketplace stay consistent (e.g. verified-only People).
  const [feat, setFeat] = useState<{ people: any[]; services: any[]; companies: any[]; jobs: any[]; projects: any[] }>(
    { people: [], services: [], companies: [], jobs: [], projects: [] });
  useEffect(() => {
    let alive = true;
    Promise.all([
      api.list("people", { limit: 6 }).catch(() => []),
      api.list("services", { limit: 6 }).catch(() => []),
      api.list("companies", { limit: 6 }).catch(() => []),
      api.list("jobs", { limit: 6 }).catch(() => []),
      api.list("projects", { limit: 6 }).catch(() => []),
    ]).then(([pe, se, co, jo, pr]) => {
      // Services API returns `fromPrice`; the card expects `from` (same mapping the Marketplace uses).
      const services = (se as any[]).map((s) => ({ ...s, from: s.fromPrice ?? s.from ?? 0 }));
      if (alive) setFeat({ people: pe, services, companies: co, jobs: jo, projects: pr });
    });
    return () => { alive = false; };
  }, []);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [category, setCategory] = useState("");
  const [, navigate] = useLocation();
  const searchRef = useRef<HTMLDivElement>(null);
  const searchTabs = ["People", "Services", "Companies", "Jobs", "Projects & Bids"];

  // Mobile-only icon category selector (matches mockup: People · Services · Jobs · More)
  const [moreOpen, setMoreOpen] = useState(false);
  const catMain: [string, any][] = [["People", UserRound], ["Services", Wrench], ["Jobs", Briefcase]];
  const catMore: [string, any][] = [["Companies", Building2], ["Projects & Bids", ClipboardList]];
  const catShort = (l: string) => (l === "Projects & Bids" ? "Projects" : l);

  // Selecting a category does NOT navigate — it just fills the search form.
  // Nothing is applied until the user presses Search.
  function pickCategory(label: string) {
    setCategory((prev) => (prev === label ? "" : label));
    searchRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  function runSearch(e?: React.FormEvent) {
    e?.preventDefault();
    const params = new URLSearchParams();
    params.set("tab", SEARCH_TAB_TO_MARKET[searchTab] || "People");
    if (q.trim()) params.set("q", q.trim());
    if (city.trim()) params.set("city", city.trim());
    // Map the friendly label to the keyword the marketplace filters on.
    const catKeyword = category ? (CATEGORY_KEYWORD[category] ?? category) : "";
    if (catKeyword.trim()) params.set("category", catKeyword.trim());
    navigate(`/marketplace?${params.toString()}`);
  }

  return (
    <div>
      <HomeBanners />
      {/* HERO */}
      <section className="bg-navy text-white relative overflow-hidden">
        {/* Building-silhouette backdrop */}
        <img src="/hero-bg.png" alt="" aria-hidden="true"
          className="pointer-events-none select-none absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/90 to-navy/40" aria-hidden="true" />

        <div className="container-x relative z-10 pt-8 sm:pt-16 grid lg:grid-cols-[1fr_1.2fr] gap-4 items-end">
          <div className="pb-6 sm:pb-16">
            {/* headline — on mobile the small workers image sits beside it */}
            <div className="flex items-end gap-1 lg:block">
              <h1 className="flex-1 min-w-0 font-extrabold leading-[1.08] text-[26px] sm:text-5xl">
                <span className="lg:hidden">Find Work.<br />Offer Services.<br />Hire Talent.<br /><span className="text-brand-gold">Win Contracts.</span></span>
                <span className="hidden lg:inline">Find Work. Offer Services.<br />Hire Talent. <span className="text-brand-gold">Win Contracts.</span></span>
              </h1>
              <img src="/hero-workers-mobile.png" alt="Filipino professionals and workers on KontrataPH"
                className="lg:hidden w-[46%] max-w-[210px] h-48 object-cover object-top self-end shrink-0 drop-shadow-xl" />
            </div>
            <p className="mt-3 sm:mt-4 text-white/70 text-sm sm:text-base max-w-lg">
              The open Filipino marketplace for people, professionals, companies, jobs, services, projects, and contracts.
            </p>
            <div className="mt-5 sm:mt-6 flex flex-col sm:flex-row lg:flex-wrap gap-2.5 sm:gap-3">
              {/* QA #12/#21: show "Join Free" only to guests; logged-in users get their dashboard. */}
              {user ? (
                <Link href="/dashboard" className="btn-red btn-lg w-full sm:w-auto justify-center">Go to Dashboard</Link>
              ) : (
                <Link href="/signup" className="btn-red btn-lg w-full sm:w-auto justify-center">
                  <span className="lg:hidden">Join Free</span><span className="hidden lg:inline">Join KontrataPH — Free</span>
                </Link>
              )}
              <Link href="/post/project" className="btn-lg w-full sm:w-auto justify-center bg-white/10 text-white border border-white/25 hover:bg-white/20 rounded-lg font-semibold px-6 inline-flex items-center">Post a Job or Project</Link>
            </div>
          </div>
          {/* Workers group (desktop) — large, bottoms flush to the hero base */}
          <div className="hidden lg:flex justify-end items-end self-end">
            <img src="/hero-workers.png" alt="Filipino professionals, tradespeople, and workers on KontrataPH"
              className="w-full object-contain object-bottom drop-shadow-2xl -mb-1" />
          </div>
        </div>

        {/* MOBILE category selector (icons) — above the search card, like the mockup */}
        <div className="lg:hidden container-x relative z-10">
          <div className="grid grid-cols-4 gap-2">
            {(moreOpen ? catMore : catMain).map(([label, Icon]) => (
              <button type="button" key={label} onClick={() => { setSearchTab(label); setMoreOpen(false); }}
                className={`flex flex-col items-center gap-1 py-1 ${searchTab === label ? "text-brand-gold" : "text-white/85"}`}>
                <span className={`w-12 h-12 rounded-xl grid place-items-center ${searchTab === label ? "bg-white text-navy" : "bg-white/10"}`}><Icon className="w-5 h-5" strokeWidth={1.8} /></span>
                <span className="text-[11px] font-semibold">{catShort(label)}</span>
              </button>
            ))}
            <button type="button" onClick={() => setMoreOpen((v) => !v)} className="flex flex-col items-center gap-1 py-1 text-white/85">
              <span className="w-12 h-12 rounded-xl grid place-items-center bg-white/10">{moreOpen ? <ArrowRight className="w-5 h-5 rotate-180" strokeWidth={1.8} /> : <MoreHorizontal className="w-5 h-5" strokeWidth={1.8} />}</span>
              <span className="text-[11px] font-semibold">{moreOpen ? "Back" : "More"}</span>
            </button>
          </div>
        </div>

        {/* SEARCH */}
        <div ref={searchRef} className="container-x relative z-10 pb-8 pt-3 lg:pt-0 lg:-mt-2">
          <form className="card p-3 text-ink" onSubmit={runSearch}>
            <div className="px-1 hidden lg:block"><Tabs tabs={searchTabs} active={searchTab} onChange={setSearchTab} /></div>
            <div className="flex flex-col sm:flex-row gap-2 p-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-3.5 text-mut" />
                <input className="field pl-9" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder={`What ${searchTab === "People" ? "skill or role" : searchTab.toLowerCase()} are you looking for?`} />
              </div>
              <div className="relative sm:w-64">
                <MapPin className="w-4 h-4 absolute left-3 top-3.5 text-mut" />
                <input className="field pl-9" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City or Province" />
              </div>
              <button type="submit" className="btn-red h-11 px-8">Search</button>
            </div>
            {category && (
              <div className="px-2 pb-1 flex items-center gap-2 text-sm">
                <span className="text-mut">Category:</span>
                <span className="chip bg-navy text-white">
                  {category}
                  <button type="button" onClick={() => setCategory("")} className="ml-1 -mr-1 hover:opacity-80" aria-label="Clear category"><X className="w-3 h-3" /></button>
                </span>
                <span className="text-xs text-mut">— press Search to apply</span>
              </div>
            )}
          </form>
        </div>

        {/* TRUST STRIP */}
        <div className="border-t border-white/10 relative z-10">
          <div className="container-x py-3 sm:py-4 grid grid-cols-4 gap-2 sm:gap-4">
            {trust.map((t) => {
              const I = trustIcons[t.icon];
              return (
                <div key={t.title} className="flex flex-col items-center text-center gap-1 sm:flex-row sm:text-left sm:gap-2.5">
                  <I className="w-5 h-5 sm:w-6 sm:h-6 text-brand-gold shrink-0" />
                  <div><div className="text-[11px] leading-tight sm:text-sm font-bold">{t.title}</div><div className="hidden sm:block text-xs text-white/60">{t.sub}</div></div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4 ACTION CARDS */}
      <section className="container-x py-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {actionCards.map((a) => {
          const I = a.icon;
          return (
            <div key={a.title} className="card p-5 flex flex-col">
              <div className={`w-11 h-11 rounded-lg grid place-items-center mb-3 ${a.color}`}><I className="w-6 h-6" /></div>
              <h3 className={`font-extrabold ${a.accent}`}>{a.title}</h3>
              <p className="text-sm text-mut mt-1 mb-3">{a.desc}</p>
              <ul className="space-y-1.5 mb-4 flex-1">
                {a.items.map((it) => <li key={it} className="text-sm text-mut flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-brand-gold" />{it}</li>)}
              </ul>
              <Link href={a.href} className={`${a.btn} w-full`}>{a.cta}</Link>
            </div>
          );
        })}
      </section>

      {/* POPULAR CATEGORIES — selecting one fills the search form; only Search navigates. */}
      <Section title="Popular Categories" action={<Link href="/marketplace" className="text-sm font-semibold text-brand-red inline-flex items-center gap-1 whitespace-nowrap">View all categories <ChevronRight className="w-4 h-4" /></Link>}>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {categories.map((c) => {
            const I = catIcons[c.icon];
            const selected = category === c.label;
            return (
              <button type="button" key={c.key} onClick={() => pickCategory(c.label)}
                aria-pressed={selected}
                className={`card p-4 flex flex-col items-center text-center gap-2 transition hover:shadow-lift ${selected ? "border-navy-500 ring-2 ring-navy/30 bg-navy-50/50" : "hover:border-navy-500"}`}>
                <div className={`w-11 h-11 rounded-lg grid place-items-center ${selected ? "bg-navy text-white" : "bg-navy-50 text-navy"}`}><I className="w-5 h-5" /></div>
                <span className="text-xs font-semibold text-navy leading-tight">{c.label}</span>
                <span className="text-[11px] text-mut">{c.count.toLocaleString()}</span>
              </button>
            );
          })}
        </div>
      </Section>

      {/* FEATURED MARKETPLACE */}
      <Section title="Featured in the Marketplace" action={<Link href="/marketplace" className="text-sm font-semibold text-brand-red inline-flex items-center gap-1 whitespace-nowrap">View all <ArrowRight className="w-4 h-4" /></Link>}>
        <div className="mb-5"><Tabs tabs={["Recommended", "People", "Services", "Companies", "Jobs", "Projects"]} active={featTab} onChange={setFeatTab} /></div>
        {(() => {
          const recommended = [
            feat.people[0] && <PersonCard key="rp" p={feat.people[0]} />,
            feat.services[0] && <ServiceCard key="rs" s={feat.services[0]} />,
            feat.companies[0] && <CompanyCard key="rc" c={feat.companies[0]} />,
            feat.jobs[0] && <JobCard key="rj" j={feat.jobs[0]} />,
            feat.projects[0] && <ProjectCard key="rpr" p={feat.projects[0]} />,
            feat.people[1] && <PersonCard key="rp2" p={feat.people[1]} />,
          ].filter(Boolean);
          const grid = {
            Recommended: recommended,
            People: feat.people.slice(0, 6).map((p) => <PersonCard key={p.id} p={p} />),
            Services: feat.services.slice(0, 6).map((s) => <ServiceCard key={s.id} s={s} />),
            Companies: feat.companies.slice(0, 6).map((c) => <CompanyCard key={c.id} c={c} />),
            Jobs: feat.jobs.slice(0, 6).map((j) => <JobCard key={j.id} j={j} />),
            Projects: feat.projects.slice(0, 6).map((p) => <ProjectCard key={p.id} p={p} />),
          }[featTab] || [];
          if (grid.length === 0) {
            return <div className="text-sm text-mut border border-dashed border-line rounded-xl p-8 text-center">Nothing to feature here yet — <Link href="/marketplace" className="text-brand-red font-semibold">browse the marketplace</Link>.</div>;
          }
          return <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{grid}</div>;
        })()}
      </Section>

      {/* STATS BAND */}
      <section className="bg-navy-50 border-y border-line">
        <div className="container-x py-10 grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
          {stats.map((s) => (
            <div key={s.lbl}><div className="text-3xl font-extrabold text-navy">{s.big}</div><div className="text-sm text-mut mt-1">{s.lbl}</div></div>
          ))}
        </div>
      </section>

      {/* BUSINESS + FINAL CTA */}
      <section className="container-x py-12 grid lg:grid-cols-2 gap-4">
        <div className="card p-8 bg-navy text-white flex flex-col justify-center">
          <h3 className="text-2xl font-extrabold">Need 10, 50, or 500 workers?</h3>
          <p className="text-white/70 mt-2">Bulk hiring, developer procurement, and project portals for enterprises and LGUs.</p>
          <div className="mt-4"><Link href="/marketplace?tab=People" className="btn-gold">Hire a Workforce</Link></div>
        </div>
        <div className="card p-8 flex flex-col justify-center">
          <h3 className="text-2xl font-extrabold text-navy">Ready to get the job done?</h3>
          <p className="text-mut mt-2">Post a job or project, or create your worker profile — free to start.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link href="/post/job" className="btn-red">Post a Job</Link>
            <Link href="/post/resume" className="btn-outline">Create Worker Profile</Link>
          </div>
        </div>
      </section>
    </div>
  );
}


function HomeBanners() {
  const [b, setB] = useState<any[]>([]);
  useEffect(() => { api.banners().then(setB).catch(() => setB([])); }, []);
  const ann = b.filter((x) => x.slot === "announcement");
  const promo = b.filter((x) => x.slot === "promo");
  if (!ann.length && !promo.length) return null;
  return (
    <>
      {ann.map((a) => (
        <a key={a.id} href={a.ctaHref || "#"} className="block bg-brand-gold/15 text-[#8a5a00] text-sm text-center py-2 px-4 hover:bg-brand-gold/25">
          <span className="font-semibold">{a.title}</span>{a.subtitle ? ` — ${a.subtitle}` : ""}{a.ctaLabel ? ` · ${a.ctaLabel} →` : ""}
        </a>
      ))}
      {promo.length > 0 && (
        <section className="container-x pt-6"><div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {promo.map((p) => (
            <a key={p.id} href={p.ctaHref || "#"} className="rounded-xl overflow-hidden border border-line relative min-h-[120px] flex flex-col justify-end p-4 text-white"
               style={{ backgroundImage: p.imageUrl ? `url(${p.imageUrl})` : "linear-gradient(135deg,#0A2540,#C8102E)", backgroundSize: "cover", backgroundPosition: "center" }}>
              <div className="absolute inset-0 bg-navy/40" />
              <div className="relative"><div className="font-bold">{p.title}</div>{p.subtitle && <div className="text-sm text-white/85">{p.subtitle}</div>}{p.ctaLabel && <div className="text-xs font-semibold mt-1 text-brand-gold">{p.ctaLabel} →</div>}</div>
            </a>
          ))}
        </div></section>
      )}
    </>
  );
}
